/**
 * OrganisationTaxSection — owner-only Company + GST registration profile
 * (Prompt 11F.8 / D14).
 *
 * Rendered only for `owner` (nav-item + this component's own guard — the
 * backend independently enforces owner-only on PATCH /v1/config/organisation
 * and /tax, same relationship as OwnerAccountsSection has to its endpoint).
 *
 * The Company block writes ORG-LEVEL data by default (inherited by every
 * tenant) — a single "Editing" mode toggle switches the save target to a
 * per-tenant override instead of exposing one override toggle per field.
 * GST registration is always per-tenant (distinct GSTINs under one PAN).
 */

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Card, CardHeader, CardTitle, Button, Input, Toggle } from '@/components/common';
import { useAuth } from '@/context';
import { useTenant } from '@core/tenant/TenantContext';
import {
  useOrganisationConfig,
  useUpdateOrganisationConfig,
  useUpdateOrganisationOverride,
  useTaxConfig,
  useUpdateTaxConfig,
  type OrganisationConfig,
  type TaxConfig,
} from '@features/settings/api/config';
import type { ApiError } from '@core/api/client';

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ModeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const ReadOnlyBanner = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} 0;
`;

interface OrgFormValues {
  legal_name: string;
  llpin: string;
  pan: string;
  registered_address: string;
  authorised_signatory: string;
  support_email: string;
  support_phone: string;
}

function toOrgFormValues(cfg: OrganisationConfig | undefined): OrgFormValues {
  return {
    legal_name: cfg?.legal_name ?? '',
    llpin: cfg?.llpin ?? '',
    pan: cfg?.pan ?? '',
    registered_address: cfg?.registered_address ?? '',
    authorised_signatory: cfg?.authorised_signatory ?? '',
    support_email: cfg?.support_email ?? '',
    support_phone: cfg?.support_phone ?? '',
  };
}

interface TaxFormValues {
  gstin: string;
  gst_state_code: string;
  place_of_business: string;
  invoice_prefix: string;
  sac_code: string;
  default_gst_rate: string;
}

function toTaxFormValues(cfg: TaxConfig | undefined): TaxFormValues {
  return {
    gstin: cfg?.gstin ?? '',
    gst_state_code: cfg?.gst_state_code ?? '',
    place_of_business: cfg?.place_of_business ?? '',
    invoice_prefix: cfg?.invoice_prefix ?? '',
    sac_code: cfg?.sac_code ?? '',
    default_gst_rate: cfg ? String(Math.round(cfg.default_gst_rate * 100)) : '',
  };
}

export const OrganisationTaxSection: React.FC = () => {
  const { user } = useAuth();
  const { isAllMode, selectedTenantId } = useTenant();
  const isOwner = user?.role === 'owner';

  const { data: orgConfig, isLoading: orgLoading } = useOrganisationConfig();
  const updateOrg = useUpdateOrganisationConfig();
  const updateOrgOverride = useUpdateOrganisationOverride();
  const { data: taxConfig, isLoading: taxLoading } = useTaxConfig();
  const updateTax = useUpdateTaxConfig();

  const [overrideMode, setOverrideMode] = useState(false);

  const orgForm = useForm<OrgFormValues>({ defaultValues: toOrgFormValues(orgConfig) });
  const taxForm = useForm<TaxFormValues>({ defaultValues: toTaxFormValues(taxConfig) });

  useEffect(() => {
    if (orgConfig) orgForm.reset(toOrgFormValues(orgConfig));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgConfig]);

  useEffect(() => {
    if (taxConfig) taxForm.reset(toTaxFormValues(taxConfig));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxConfig]);

  if (!isOwner) return null;

  const onOrgSubmit = orgForm.handleSubmit(async (values) => {
    const mutation = overrideMode ? updateOrgOverride : updateOrg;
    try {
      const saved = await mutation.mutateAsync(values);
      orgForm.reset(toOrgFormValues(saved));
      toast.success(
        overrideMode ? `Company profile overridden for ${selectedTenantId}.` : 'Company profile updated for all schools.',
      );
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not update the company profile.');
    }
  });

  const onTaxSubmit = taxForm.handleSubmit(async (values) => {
    const rate = values.default_gst_rate === '' ? undefined : Number(values.default_gst_rate) / 100;
    try {
      const saved = await updateTax.mutateAsync({
        gstin: values.gstin || null,
        gst_state_code: values.gst_state_code || null,
        place_of_business: values.place_of_business || null,
        invoice_prefix: values.invoice_prefix || null,
        sac_code: values.sac_code || null,
        default_gst_rate: rate,
        gst_registered: taxConfig?.gst_registered ?? false,
      });
      taxForm.reset(toTaxFormValues(saved));
      toast.success('GST registration profile updated.');
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 422 && apiErr.fieldErrors?.length) {
        apiErr.fieldErrors.forEach(({ field, message }) => {
          taxForm.setError(field as keyof TaxFormValues, { type: 'server', message });
        });
      } else {
        toast.error(apiErr.message || 'Could not update the GST profile.');
      }
    }
  });

  const toggleGstRegistered = async () => {
    try {
      const saved = await updateTax.mutateAsync({ gst_registered: !(taxConfig?.gst_registered ?? false) });
      taxForm.reset(toTaxFormValues(saved));
      toast.success(saved.gst_registered ? 'Marked GST-registered.' : 'Marked GST-exempt.');
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not update GST registration status.');
    }
  };

  return (
    <>
      {isAllMode && (
        <ReadOnlyBanner data-testid="candeza-org-tax-all-mode-banner">
          Select a school to edit the Organisation &amp; Tax profile.
        </ReadOnlyBanner>
      )}

      <Card padding="lg" data-testid="candeza-org-profile" style={{ marginBottom: 24 }}>
        <CardHeader>
          <CardTitle>Company — {selectedTenantId}</CardTitle>
        </CardHeader>
        <p style={{ fontSize: 14, color: '#64748b', marginTop: 0 }}>
          Legal entity behind every school. Fields saved as "All schools" are inherited by every
          tenant; switch to "This school only" to override just {selectedTenantId}.
        </p>

        <ModeRow>
          <span>Editing:</span>
          <Button
            type="button"
            variant={overrideMode ? 'outline' : 'primary'}
            size="sm"
            onClick={() => setOverrideMode(false)}
            disabled={isAllMode}
          >
            All schools
          </Button>
          <Button
            type="button"
            variant={overrideMode ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setOverrideMode(true)}
            disabled={isAllMode}
            data-testid="candeza-org-override-toggle"
          >
            This school only
          </Button>
        </ModeRow>

        <form onSubmit={onOrgSubmit} noValidate>
          <FormGrid>
            <Input
              label="Legal name"
              disabled={isAllMode || orgLoading}
              {...orgForm.register('legal_name')}
              data-testid="candeza-org-legal-name"
            />
            <Input
              label="LLPIN"
              disabled={isAllMode || orgLoading}
              {...orgForm.register('llpin')}
              data-testid="candeza-org-llpin"
            />
            <Input
              label="PAN"
              disabled={isAllMode || orgLoading}
              {...orgForm.register('pan')}
              data-testid="candeza-org-pan"
            />
            <Input
              label="Authorised signatory"
              disabled={isAllMode || orgLoading}
              {...orgForm.register('authorised_signatory')}
              data-testid="candeza-org-signatory"
            />
            <Input
              label="Support email"
              type="email"
              disabled={isAllMode || orgLoading}
              {...orgForm.register('support_email')}
              data-testid="candeza-org-support-email"
            />
            <Input
              label="Support phone"
              disabled={isAllMode || orgLoading}
              {...orgForm.register('support_phone')}
              data-testid="candeza-org-support-phone"
            />
            <Input
              label="Registered address"
              disabled={isAllMode || orgLoading}
              {...orgForm.register('registered_address')}
              data-testid="candeza-org-address"
            />
          </FormGrid>
          <FormActions>
            <Button
              type="submit"
              disabled={isAllMode || orgForm.formState.isSubmitting || !orgForm.formState.isDirty}
              data-testid="candeza-settings-save-organisation"
            >
              {orgForm.formState.isSubmitting ? 'Saving…' : 'Save Changes'}
            </Button>
          </FormActions>
        </form>
      </Card>

      <Card padding="lg" data-testid="candeza-tax-profile">
        <CardHeader>
          <CardTitle>GST registration — {selectedTenantId}</CardTitle>
        </CardHeader>
        <p style={{ fontSize: 14, color: '#64748b', marginTop: 0 }}>
          Always specific to this school — RNB and HNS hold distinct GSTINs under one PAN.
        </p>

        <ToggleRow>
          <span>GST registered</span>
          <Toggle
            active={taxConfig?.gst_registered ?? false}
            onToggle={toggleGstRegistered}
            disabled={isAllMode || taxLoading}
            data-testid="candeza-tax-gst-registered-toggle"
          />
        </ToggleRow>
        {!taxConfig?.gst_registered && (
          <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 0 }}>
            Invoices for this school will render "Exempt" until GST registration is turned on.
          </p>
        )}

        <form onSubmit={onTaxSubmit} noValidate>
          <FormGrid style={{ marginTop: 12 }}>
            <div>
              <Input
                label="GSTIN"
                disabled={isAllMode || taxLoading}
                error={taxForm.formState.errors.gstin?.message}
                {...taxForm.register('gstin')}
                data-testid="candeza-tax-gstin"
              />
            </div>
            <div>
              <Input
                label="GST state code"
                disabled={isAllMode || taxLoading}
                error={taxForm.formState.errors.gst_state_code?.message}
                {...taxForm.register('gst_state_code')}
                data-testid="candeza-tax-state-code"
              />
            </div>
            <Input
              label="Place of business"
              disabled={isAllMode || taxLoading}
              {...taxForm.register('place_of_business')}
              data-testid="candeza-tax-place-of-business"
            />
            <Input
              label="Invoice prefix"
              disabled={isAllMode || taxLoading}
              {...taxForm.register('invoice_prefix')}
              data-testid="candeza-tax-invoice-prefix"
            />
            <Input
              label="SAC code"
              disabled={isAllMode || taxLoading}
              {...taxForm.register('sac_code')}
              data-testid="candeza-tax-sac-code"
            />
            <Input
              label="Default GST rate (%)"
              type="number"
              step="0.01"
              disabled={isAllMode || taxLoading}
              {...taxForm.register('default_gst_rate')}
              data-testid="candeza-tax-default-rate"
            />
          </FormGrid>
          <FormActions>
            <Button
              type="submit"
              disabled={isAllMode || taxForm.formState.isSubmitting || !taxForm.formState.isDirty}
              data-testid="candeza-settings-save-tax"
            >
              {taxForm.formState.isSubmitting ? 'Saving…' : 'Save Changes'}
            </Button>
          </FormActions>
        </form>
      </Card>
    </>
  );
};

export default OrganisationTaxSection;
